import Cookies from 'js-cookie';
import React, { createContext, useContext, useMemo } from 'react';
import UserInitializationContext from './user-initialization-context';

const SLATE_THEME_OVERRIDE_COOKIE = 'cvna-slate-theme-override';

const isSlateThemeOverrideEnabled = (): boolean =>
  process.env.NODE_ENV !== 'production' && Cookies.get(SLATE_THEME_OVERRIDE_COOKIE) === 'true';

const getIsSlateSkin = (): boolean => {
  const slateDomain = process.env.SLATE_DOMAIN;
  if (!slateDomain) return false;
  return location.hostname.toLowerCase() === slateDomain.toLowerCase();
};

const isSlateVehicle = (inventoryType?: string | null): boolean => {
  return inventoryType?.toLowerCase() === 'slate';
};

interface SlateContextValue {
  isSlateSkin: boolean;
  isSlateVehicle: boolean;
}

const SlateContext = createContext<SlateContextValue>({
  isSlateSkin: false,
  isSlateVehicle: false
});

interface SlateProviderProps {
  inventoryType?: string | null;
}

const SlateProvider: React.FC<React.PropsWithChildren<SlateProviderProps>> = ({
  inventoryType,
  children
}) => {
  const { isImpersonating } = useContext(UserInitializationContext);
  const value = useMemo(() => {
    const overrideEnabled = isSlateThemeOverrideEnabled();
    const slateVehicle = isSlateVehicle(inventoryType);
    return {
      isSlateSkin: overrideEnabled || (isImpersonating && slateVehicle) || getIsSlateSkin(),
      isSlateVehicle: overrideEnabled || slateVehicle
    };
  }, [inventoryType, isImpersonating]);

  return <SlateContext.Provider value={value}>{children}</SlateContext.Provider>;
};

const useSlate = () => useContext(SlateContext);

export default SlateContext;
export { SlateProvider, useSlate, SLATE_THEME_OVERRIDE_COOKIE };
